import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, StatCard } from "../components/PageHeader";
import api from "../lib/api";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, User, ClipboardText } from "@phosphor-icons/react";

// Fix Leaflet default marker icons (Webpack strips assets)
const INDIA_CENTER = [20.5937, 78.9629]; // stable ref so the map doesn't reset zoom on re-renders

const brandIcon = L.divIcon({
  className: "greenrgy-pin",
  html:
    `<div style="position:relative;width:28px;height:36px;">
       <div style="position:absolute;inset:0;background:#106109;border:2px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,.35);"></div>
       <div style="position:absolute;top:8px;left:8px;width:12px;height:12px;background:#fff;border-radius:50%;"></div>
     </div>`,
  iconSize: [28, 36],
  iconAnchor: [14, 34],
  popupAnchor: [0, -32],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0][0], points[0][1]], 12);
    } else {
      map.fitBounds(points, { padding: [50, 50] });
    }
  }, [points, map]);
  return null;
}

export default function FieldMap() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [department, setDepartment] = useState("Sales");
  const [departments, setDepartments] = useState([]);
  const [data, setData] = useState({ locations: [], count: 0 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: field }, { data: st }] = await Promise.all([
        api.get("/reports/field-locations", {
          params: { date_from: date, date_to: date, department: department || undefined },
        }),
        api.get("/settings"),
      ]);
      setData(field);
      setDepartments((st.department || []).map((d) => d.value));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [date, department]);

  const points = useMemo(
    () => (data.locations || [])
      .filter((l) => l.location_lat != null && l.location_lng != null)
      .map((l) => [l.location_lat, l.location_lng]),
    [data]
  );

  const uniqueEmployees = useMemo(() => {
    const s = new Set((data.locations || []).map((l) => l.employee_id));
    return s.size;
  }, [data]);

  return (
    <div>
      <PageHeader
        eyebrow="Field Coverage"
        title="Live Field Map"
        subtitle="Pinned locations from today's field reports so managers see coverage at a glance."
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Controls */}
        <div className="flex flex-wrap items-end gap-4 bg-white border border-[#E5E7EB] p-4">
          <div>
            <div className="label-caps mb-1.5">Date</div>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   data-testid="fieldmap-date" className="border border-[#E5E7EB] px-3 py-2 text-sm" />
          </div>
          <div>
            <div className="label-caps mb-1.5">Department</div>
            <select value={department} onChange={(e) => setDepartment(e.target.value)}
                    data-testid="fieldmap-dept" className="border border-[#E5E7EB] px-3 py-2 text-sm">
              <option value="">All departments</option>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs text-[#6B7280]">
            <span className="inline-block w-2.5 h-2.5 bg-[#106109] rounded-full" />
            Visit pin
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label="Locations Pinned" value={data.count || 0} testId="stat-locations" />
          <StatCard label="Employees On Field" value={uniqueEmployees} testId="stat-field-employees" />
          <StatCard label="Selected Day" value={date} testId="stat-selected-day" />
        </div>

        {/* Map */}
        <div className="bg-white border border-[#E5E7EB]" data-testid="field-map-container">
          <div style={{ height: 520, width: "100%" }}>
            <MapContainer
              center={INDIA_CENTER}
              zoom={5}
              scrollWheelZoom={true}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {points.length > 0 && <FitBounds points={points} />}
              {(data.locations || []).map((l) => (
                <Marker key={l.id} position={[l.location_lat, l.location_lng]} icon={brandIcon}>
                  <Popup>
                    <div style={{ minWidth: 200 }}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>{l.title}</div>
                      <div style={{ fontSize: 12, color: "#374151" }}>
                        <User size={12} /> {l.employee_name} ({l.employee_code}) · {l.department}
                      </div>
                      {l.location_address && (
                        <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
                          <MapPin size={12} /> {l.location_address}
                        </div>
                      )}
                      <div style={{ fontSize: 11, fontFamily: "monospace", color: "#6B7280", marginTop: 4 }}>
                        {l.location_lat.toFixed(5)}, {l.location_lng.toFixed(5)}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>

        {/* List below map */}
        <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr className="text-left">
                {["Employee", "Report", "Address", "Coordinates"].map((h) => (
                  <th key={h} className="px-4 py-3 label-caps">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.locations || []).map((l) => (
                <tr key={l.id} className="border-b border-[#F3F4F6]" data-testid={`fieldmap-row-${l.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{l.employee_name}</div>
                    <div className="text-xs text-[#6B7280]">{l.employee_code} · {l.department}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-1"><ClipboardText size={12} /> {l.title}</div>
                  </td>
                  <td className="px-4 py-3 text-[#6B7280]">{l.location_address || "—"}</td>
                  <td className="px-4 py-3">
                    <a href={`https://www.google.com/maps?q=${l.location_lat},${l.location_lng}`}
                       target="_blank" rel="noopener noreferrer"
                       className="text-xs font-mono text-[#106109] hover:underline">
                      {l.location_lat.toFixed(5)}, {l.location_lng.toFixed(5)} ↗
                    </a>
                  </td>
                </tr>
              ))}
              {(data.locations || []).length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-16 text-center text-[#9CA3AF]">
                    {loading ? "Loading…" : "No pinned locations for the selected day."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
